// src/pages/administrativo/ConfigModuloCad.js
import React, { useState, useEffect, useRef } from 'react';

import Input from '../../components/input/Input.js';
import { useConfigModulo } from '../../contexts/administrativo/ConfigModuloContext.js';
import ConfigModuloSearch from './ConfigModuloSearch';
import { ConfigModulo, DFePadraoEnum } from '../../models/administrativo/ConfigModulo.js';
import { useMessage } from '../../contexts/geral/MessageContext.js';
import { useYesNo } from '../../contexts/geral/YesNoContext.js';
import ModalContainer from '../../components/modalForm/ModalContainer.js';
import ModalHeader from '../../components/modalForm/ModalHeader.js';
import ModalBody from '../../components/modalForm/ModalBody.js';
import InputCheck from '../../components/input/InputCheck.js';
import Select from '../../components/input/Select.js';
import ModalFooter from '../../components/modalForm/ModalFooter';
import ModalActions, { ModalActionButton } from '../../components/modalForm/ModalActions';

const ConfigModuloCad = ({ show, onHide }) => {
    const { configModuloList, addConfigModulo, updateConfigModulo, deleteConfigModulo } = useConfigModulo();
    const [configModulo, setConfigModulo] = useState(new ConfigModulo());
    const [showSearchModal, setShowSearchModal] = useState(false);
    const {showMessage} = useMessage();
    const { showYesNo } = useYesNo();
    const [novoReg, setNovoReg] = useState(false);
    const [abaAtiva, setAbaAtiva] = useState('geral');
    const seletorCorAppRef = useRef(null);
    const seletorCorFonteRef = useRef(null);

    useEffect(() => {
        setConfigModulo(new ConfigModulo());
    }, []);

    const handleNovo = () => {
        setConfigModulo(new ConfigModulo());
        setNovoReg(true);
    };

    const handleEditar = (id) => {
        if (id === 0) {
            showMessage("Selecione uma configuração de módulo para editar!");            
            return;
        }
        setNovoReg(false);
    };

    const handleDelete = async () => {
        if (!configModulo?.id) {
            showMessage("Selecione uma configuração de módulo para excluir!");            
        } else {
            const resposta = await showYesNo("Deseja excluir a configuração do sistema selecionada?");
            if (resposta) {
                await deleteConfigModulo(configModulo.id);
                handleNovo();
            }
        }
    };

    const handleSave = async () => {
        try {
            if (!configModulo.filialId) {
                showMessage("Filial não preenchida!", 'warning');                
                return;
            }

            if (!configModulo.id) {
                await addConfigModulo(configModulo);
            } else {
                await updateConfigModulo(configModulo);
            }
            handleNovo();
        } catch (error) {
            console.error("Erro ao gravar dados: ", error);
        }
    };

    const handleExit = () => {
        const configSelecionada = configModuloList.find(c => c.id.toString() === configModulo?.id?.toString());

        if (configSelecionada) {
            setConfigModulo(configSelecionada);
            handleEditar(configModulo?.id);
        } else {
            setConfigModulo(new ConfigModulo());
        }
    };

    // const coresDisponiveis = [
    //     "#d81541", "#007bff", "#28a745", "#ffc107", "#6f42c1", "#17a2b8", "#343a40", "#fd7e14"
    // ];

    const coresDisponiveis = [
        "#d81541", // vermelho iFood
        "#ff5722", // laranja vibrante (apetite)
        "#ffc107", // amarelo mostarda (hambúrguer/pão)
        "#4caf50", // verde (confiança, natural)
        "#00bcd4", // azul turquesa (refrescante)
        "#FF6F00", // laranja queimado (delivery rápido)
        "#795548", // marrom (chocolate, café, calor)
        "#f44336", // vermelho tomate
        "#9c27b0", // roxo vibrante (moderno e ousado)
    ];

    const coresFonteDisponiveis = [
        "#FFFFFF", // branco - texto sobre fundo escuro
        "#F5F5F5", // branco gelo - suave
        "#E0E0E0", // cinza claro
        "#BDBDBD", // cinza médio
        "#9E9E9E", // cinza escuro
        "#757575", // grafite médio
        "#424242", // cinza quase preto
        "#212121", // preto suave
        "#000000", // preto total
    ];

    const normalizarCorHex = (valor) => {
        if (!valor) {
            return '#000000';
        }

        let cor = valor.toString().trim().toUpperCase();

        if (!cor.startsWith('#')) {
            cor = `#${cor}`;
        }

        if (/^#[0-9A-F]{6}$/.test(cor)) {
            return cor;
        }

        return cor;
    };

    const renderCorSelecionada = (corSelecionada, coresBase, onSelectCor, seletorRef, textoBotao) => {
        const corHexValida = /^#[0-9A-F]{6}$/i.test(corSelecionada || '');
        const corExibicao = corHexValida ? normalizarCorHex(corSelecionada) : '#000000';

        return (
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                {coresBase.map(cor => (
                    <div
                        key={cor}
                        onClick={() => onSelectCor(normalizarCorHex(cor))}
                        title={cor}
                        style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '4px',
                            backgroundColor: cor,
                            border: corExibicao === normalizarCorHex(cor) ? '3px solid #000' : '1px solid #ccc',
                            cursor: 'pointer'
                        }}
                    />
                ))}
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => seletorRef.current?.click()}
                    style={{ minWidth: '110px', height: '30px', marginTop: '24px' }}
                >
                    {textoBotao}
                </button>
                <input
                    ref={seletorRef}
                    type="color"
                    value={corExibicao}
                    onChange={(e) => onSelectCor(normalizarCorHex(e.target.value))}
                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                    tabIndex={-1}
                    aria-hidden="true"
                />
                <div style={{ marginTop: '-6px' }}>
                    <Input
                        label="HEX"
                        value={normalizarCorHex(corSelecionada)}
                        onChange={(e) => onSelectCor(e.target.value.toUpperCase())}
                        onBlur={(e) => onSelectCor(normalizarCorHex(e.target.value))}
                        placeholder="#RRGGBB"
                        size={110}
                        style={{ textTransform: 'uppercase' }}
                    />
                </div>
                <div
                    title={corExibicao}
                    style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '4px',
                        backgroundColor: corExibicao,
                        border: !coresBase.includes(corExibicao) ? '2px dashed #999' : '3px solid #000',
                        cursor: 'default'
                    }}
                />
            </div>
        );
    };

    if (!show) return null;

    return (
        <ModalContainer show={show} largura='850px' variant="cadastro" onHide={onHide}>
            <ModalHeader title={novoReg ? 'Nova Configuração de Módulo' : 'Alterar Configuração de Módulo'} onHide={onHide} />
            <ModalBody>
                <div className="input-group mb-3">
                    <Input
                        label="Código"
                        value={configModulo.id || ''}
                        onChange={(e) => setConfigModulo({ ...configModulo, id: e.target.value })}
                        onBlur={handleExit}
                        disabled={!novoReg}
                        btnBuscar={() => setShowSearchModal(true)}
                    />
                    <Input
                        label="Nome do Estabelecimento"
                        value={configModulo.nomeEstabelecimento || ''}
                        onChange={(e) => setConfigModulo({ ...configModulo, nomeEstabelecimento: e.target.value })}
                        size={300}
                        autoFocus={true}
                    />
                </div>
                <div className="mb-3 d-flex">
                    <button 
                        className={`btn me-2 ${abaAtiva === 'geral' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={(e) => { e.preventDefault(); setAbaAtiva('geral'); }}
                    >
                        Geral
                    </button>
                    <button
                        className={`btn me-2 ${abaAtiva === 'financeiro' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={(e) => { e.preventDefault(); setAbaAtiva('financeiro'); }}
                    >
                        Financeiro
                    </button>
                    <button
                        className={`btn me-2 ${abaAtiva === 'servicos' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={(e) => { e.preventDefault(); setAbaAtiva('servicos'); }}
                    >
                        Serviços
                    </button>
                    <button 
                        className={`btn me-2 ${abaAtiva === 'pedidos' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={(e) => { e.preventDefault(); setAbaAtiva('pedidos'); }}
                    >
                        Pedidos
                    </button>
                    <button 
                        className={`btn me-2 ${abaAtiva === 'appDelivery' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={(e) => { e.preventDefault(); setAbaAtiva('appDelivery'); }}
                    >
                        App Delivery
                    </button>
                    <button 
                        className={`btn me-2 ${abaAtiva === 'appAndroid' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={(e) => { e.preventDefault(); setAbaAtiva('appAndroid'); }}
                    >
                        App Android
                    </button>
                    <button 
                        className={`btn ${abaAtiva === 'impressao' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={(e) => { e.preventDefault(); setAbaAtiva('impressao'); }}
                    >
                        Impressão
                    </button>
                </div>                
                <form>                    
                    {abaAtiva === 'geral' && (
                    <>
                        <div className="input-group mb-3">
                            <Input
                                label="Filial"
                                value={configModulo.filialId || ''}
                                onChange={(e) => setConfigModulo({ ...configModulo, filialId: e.target.value })}
                                size={100}
                            />
                            <Select 
                                label="Módulo"
                                value={configModulo.modulo}
                                size={170}
                                onChange={(e) => setConfigModulo({ ...configModulo, modulo: Number(e.target.value) })}
                                options={[{ value: '1', label: 'Bar' }, { value: '2', label: 'Mercado' }]}
                                style={{ marginRight: '20px' }}
                            />
                            <InputCheck
                                label="Controla Estoque"
                                checked={configModulo.controleEstoque}
                                onChange={(e) => setConfigModulo({ ...configModulo, controleEstoque: e.target.checked })}
                            />
                            <InputCheck
                                label="Emite NFC-e"
                                checked={configModulo.emiteNFCe}
                                onChange={(e) => setConfigModulo({ ...configModulo, emiteNFCe: e.target.checked })}
                            />
                            <InputCheck
                                label="Emite NF-e"
                                checked={configModulo.emiteNFe}
                                onChange={(e) => setConfigModulo({ ...configModulo, emiteNFe: e.target.checked })}
                            />
                            <Select
                                label="DFe padrão"
                                value={configModulo.dFePadrao}
                                size={120}
                                onChange={(e) => setConfigModulo({ ...configModulo, dFePadrao: Number(e.target.value) })}
                                options={[
                                    { value: DFePadraoEnum.NFCe, label: 'NFC-e' },
                                    { value: DFePadraoEnum.NFe, label: 'NF-e' }
                                ]}
                            />
                            <InputCheck
                                label="Emite MDF-e"
                                checked={!!configModulo.emiteMDFe}
                                onChange={(e) => setConfigModulo({ ...configModulo, emiteMDFe: e.target.checked })}
                            />
                            <InputCheck
                                label="Imprimir Valor Unitário no Recibo"
                                checked={configModulo.printValUnRec}
                                onChange={(e) => setConfigModulo({ ...configModulo, printValUnRec: e.target.checked })}
                            />

                            <InputCheck
                                label="Mensalidade"
                                checked={!!configModulo.mensalidade}
                                onChange={(e) => setConfigModulo({ ...configModulo, mensalidade: e.target.checked })}
                            />
                            <InputCheck
                                label="Criar Cliente pelo WhatsApp"
                                checked={!!configModulo.criaClienteWhats}
                                onChange={(e) => setConfigModulo({ ...configModulo, criaClienteWhats: e.target.checked })}
                            />   

                        </div>
                    </>
                )}

                {abaAtiva === 'financeiro' && (
                    <div className="input-group mb-3">
                        <InputCheck
                            id="usaFinanceiro"
                            label="Usa Financeiro"
                            checked={!!configModulo.usaFinanceiro}
                            onChange={(e) => setConfigModulo({ ...configModulo, usaFinanceiro: e.target.checked })}
                        />
                    </div>
                )}

                {abaAtiva === 'servicos' && (
                    <div className="input-group mb-3 gap-3">
                        <InputCheck
                            label="Usa OS"
                            checked={!!configModulo.usaOS}
                            onChange={(e) => setConfigModulo({ ...configModulo, usaOS: e.target.checked })}
                        />
                        <InputCheck
                            id="usaPrioridadeOS"
                            label="Usa prioridade na OS"
                            checked={!!configModulo.usaPrioridadeOS}
                            onChange={(e) => setConfigModulo({ ...configModulo, usaPrioridadeOS: e.target.checked })}
                        />
                    </div>
                )}

                {abaAtiva === 'pedidos' && (
                    <>
                        <div className="input-group mb-3">
                            <InputCheck
                                label="Enviar QR Code PIX"
                                checked={!!configModulo.enviarQrCodePix}
                                onChange={(e) => setConfigModulo({ ...configModulo, enviarQrCodePix: e.target.checked })}
                            />
                        </div>
                        {configModulo.enviarQrCodePix && (
                            <>
                                <div className="input-group mb-3">
                                    <Select 
                                        label="Tipo PIX"
                                        value={configModulo.tipoPix}
                                        size={120}
                                        onChange={(e) => setConfigModulo({ ...configModulo, tipoPix: e.target.value })}
                                        options={[
                                            { value: 'Telefone', label: 'Telefone' },
                                            { value: 'CPF', label: 'CPF' },
                                            { value: 'CNPJ', label: 'CNPJ' },
                                            { value: 'E-mail', label: 'E-mail' }
                                        ]}
                                        style={{ marginRight: '20px' }}
                                    />
                                    <Input
                                        label="Chave PIX"
                                        value={configModulo.chavePix || ''}
                                        onChange={(e) => setConfigModulo({ ...configModulo, chavePix: e.target.value })}
                                        size={250}
                                    />
                                </div>
                                <div className="input-group mb-3">
                                    <Input
                                        label="Nome do Recebedor"
                                        value={configModulo.nomeRecebedor || ''}
                                        onChange={(e) => setConfigModulo({ ...configModulo, nomeRecebedor: e.target.value })}
                                        size={200}
                                    />
                                    <Input
                                        label="Cidade do Recebedor"
                                        value={configModulo.cidadeRecebedor || ''}
                                        onChange={(e) => setConfigModulo({ ...configModulo, cidadeRecebedor: e.target.value })}
                                        size={200}
                                    />
                                </div>
                            </>
                        )}

                        <div className="input-group mb-3">  
                            <InputCheck
                                label="Módulo Pedido"
                                checked={configModulo.moduloPedido}
                                onChange={(e) => setConfigModulo({ ...configModulo, moduloPedido: e.target.checked })}
                            />
                            <InputCheck
                                label="Taxa de Entrega por Bairro"
                                checked={!!configModulo.taxaEntregaBairro}
                                onChange={(e) => setConfigModulo({ ...configModulo, taxaEntregaBairro: e.target.checked })}
                            />
                            <InputCheck
                                label="Controla Mesa"
                                checked={configModulo.controleMesa}
                                onChange={(e) => setConfigModulo({ ...configModulo, controleMesa: e.target.checked })}
                            />
                            <InputCheck
                                label="Pedido Gera Nota"
                                checked={configModulo.pedidoGeraNota}
                                onChange={(e) => setConfigModulo({ ...configModulo, pedidoGeraNota: e.target.checked })}
                            />
                            <InputCheck
                                label="Exibir Concluído"
                                checked={configModulo.exibirConcluido}
                                onChange={(e) => setConfigModulo({ ...configModulo, exibirConcluido: e.target.checked })}
                            />
                            <InputCheck
                                label="Exibir Origem do Pedido"
                                checked={configModulo.exibirOrigemPedido}
                                onChange={(e) => setConfigModulo({ ...configModulo, exibirOrigemPedido: e.target.checked })}                                
                            />
                            <InputCheck
                                label="Zerar Pedido Dia"
                                checked={configModulo.usaIdDia}
                                onChange={(e) => setConfigModulo({ ...configModulo, usaIdDia: e.target.checked })}                                
                            />
                        </div>
                    </>
                )}

                {abaAtiva === 'impressao' && (
                    <>
                        <div className="input-group mb-3">
                            <InputCheck
                                label="Monitor Desktop"
                                checked={!!configModulo.monitorDesktop}
                                onChange={(e) =>
                                    setConfigModulo({ ...configModulo, monitorDesktop: e.target.checked })
                                }
                            />
                            <InputCheck
                                label="Monitor Web"
                                checked={!!configModulo.monitorWeb}
                                onChange={(e) =>
                                    setConfigModulo({ ...configModulo, monitorWeb: e.target.checked })
                                }
                            />
                            <InputCheck
                                label="Imprimir do Celular"
                                checked={configModulo.printCelular}
                                onChange={(e) => setConfigModulo({ ...configModulo, printCelular: e.target.checked })}
                            />
                            <InputCheck
                                label="Pergunta Impressão"
                                checked={!!configModulo.perguntaImpressao}
                                onChange={(e) => setConfigModulo({ ...configModulo, perguntaImpressao: e.target.checked })}
                            />
                            <InputCheck
                                label="Imprimir Pedido ao Aceitar"
                                checked={configModulo.printPedidoAceito}
                                onChange={(e) => setConfigModulo({ ...configModulo, printPedidoAceito: e.target.checked })}
                            />
                            <InputCheck
                                label="Imprimir Previsão"
                                checked={!!configModulo.imprimePrevisao}
                                onChange={(e) =>
                                    setConfigModulo({ ...configModulo, imprimePrevisao: e.target.checked })
                                }
                            />
                            <InputCheck
                                label="Imprimir Horário"
                                checked={!!configModulo.imprimeHorario}
                                onChange={(e) =>
                                    setConfigModulo({ ...configModulo, imprimeHorario: e.target.checked })
                                }
                            />
                            <InputCheck
                                label="Imprimir Pedido Avulso"
                                checked={!!configModulo.imprimirPedidoAvulso}
                                onChange={(e) =>
                                    setConfigModulo({ ...configModulo, imprimirPedidoAvulso: e.target.checked })
                                }
                            />
                            <InputCheck
                                id="comprovantePagComanda"
                                label="Imprimir comprovante de pagamento da comanda"
                                checked={!!configModulo.comprovantePagComanda}
                                onChange={(e) =>
                                    setConfigModulo({ ...configModulo, comprovantePagComanda: e.target.checked })
                                }
                            />
                            <Input
                                label="Descrição do evento no ticket"
                                value={configModulo.eventoTicketDesc || ''}
                                onChange={(e) =>
                                    setConfigModulo({ ...configModulo, eventoTicketDesc: e.target.value })
                                }
                                maxLength={200}
                                size={420}
                            />

                        </div>
                    </>
                )}

                {abaAtiva === 'appDelivery' && (
                    <>
                        <div className="form-group mb-3">
                            <label className="form-label" style={{marginBottom:'-22px'}}>Cor App</label>
                            {renderCorSelecionada(
                                configModulo.corApp,
                                coresDisponiveis,
                                (cor) => setConfigModulo({ ...configModulo, corApp: cor }),
                                seletorCorAppRef,
                                'Mais cores'
                            )}
                        </div>

                        <div className="form-group mb-3">
                            <label className="form-label" style={{marginBottom:'-22px'}}>Cor da Fonte</label>
                            {renderCorSelecionada(
                                configModulo.corFonteApp,
                                coresFonteDisponiveis,
                                (cor) => setConfigModulo({ ...configModulo, corFonteApp: cor }),
                                seletorCorFonteRef,
                                'Mais cores'
                            )}
                        </div>
                    </>
                )}

                {abaAtiva === 'appAndroid' && (
                    <>
                        <div className="input-group mb-3">
                            <InputCheck
                                label="Usa Painel Pedido"
                                checked={!!configModulo.painelPedidoAndroid}
                                onChange={(e) => setConfigModulo({ ...configModulo, painelPedidoAndroid: e.target.checked })}
                            />
                            <InputCheck
                                label="Monitor TEF"
                                checked={!!configModulo.androidMonitorTef}
                                onChange={(e) => setConfigModulo({ ...configModulo, androidMonitorTef: e.target.checked })}
                            />
                            <InputCheck
                                label="Emitir DFE"
                                checked={!!configModulo.androidEmiteDFE}
                                onChange={(e) => setConfigModulo({ ...configModulo, androidEmiteDFE: e.target.checked })}
                            />
                            <InputCheck
                                label="Mesas"
                                checked={!!configModulo.androidMesa}
                                onChange={(e) => setConfigModulo({ ...configModulo, androidMesa: e.target.checked })}
                            />
                            <InputCheck
                                label="Comandas"
                                checked={!!configModulo.androidComanda}
                                onChange={(e) => setConfigModulo({ ...configModulo, androidComanda: e.target.checked })}
                            />
                            <InputCheck
                                label="Fechar Comanda"
                                checked={!!configModulo.androidFechaComanda}
                                onChange={(e) => setConfigModulo({ ...configModulo, androidFechaComanda: e.target.checked })}
                            />
                            <InputCheck
                                label="Testar TEF"
                                checked={!!configModulo.testeTEF}
                                onChange={(e) => setConfigModulo({ ...configModulo, testeTEF: e.target.checked })}
                            />
                            <InputCheck
                                label="Atualizar Dados"
                                checked={!!configModulo.androidAtualizaDados}
                                onChange={(e) => setConfigModulo({ ...configModulo, androidAtualizaDados: e.target.checked })}
                            />
                            <InputCheck
                                label="Seleciona Grupo/Produto"
                                checked={!!configModulo.selGrupoProduto}
                                onChange={(e) => setConfigModulo({ ...configModulo, selGrupoProduto: e.target.checked })}
                            />
                        </div>
                    </>
                )}

                </form>
            </ModalBody>
            <ModalFooter className="modal-footer justify-content-end">
                <ModalActions >
                    <ModalActionButton className="btn btn-primary" onClick={handleSave} action="save">
                        Salvar
                    </ModalActionButton>
                    <ModalActionButton className="btn btn-danger" onClick={handleDelete} disabled={novoReg} action="delete">
                        Excluir
                    </ModalActionButton>
                    <ModalActionButton className="btn btn-secondary" onClick={handleNovo} disabled={novoReg} action="new">
                        Novo
                    </ModalActionButton>
                </ModalActions>
            </ModalFooter>

            <ConfigModuloSearch
                show={showSearchModal}
                onHide={() => setShowSearchModal(false)}
                onSelectConfigModulo={(configSel) => {
                    setConfigModulo(configSel);
                    setShowSearchModal(false);
                    handleEditar(configSel.id);
                }}
            />
        </ModalContainer>
    );
};

export default ConfigModuloCad;

